/**
 * TBB Curated By Badge — avatar hydration
 *
 * Mirrors the rankings widget pattern: fetches the seller's profileImage
 * (Cloudinary, served by the PV API via the curator-rankings Worker) and
 * swaps it into the badge avatar. Server-side render uses Shopify's
 * collection.image as a graceful baseline; this script upgrades it.
 *
 * - Idempotent: bails if already initialized for the badge node.
 * - Multi-instance safe: handles every [data-tbb-curated-by] on page.
 * - Cache-friendly: relies on the Worker's per-vendor endpoint cache.
 * - Fail-soft: any error silently leaves the server-rendered avatar.
 */
(function () {
  'use strict';

  if (window.__tbbCuratedByInit) return;
  window.__tbbCuratedByInit = true;

  /**
   * localStorage cache so repeat PDP visits skip the network entirely.
   * Keyed by vendor handle. 24h TTL. Failsafe — if storage is unavailable
   * (Safari private mode, quota exceeded), cache calls become no-ops.
   */
  var CACHE_PREFIX = 'tbb-curated-by:';
  var CACHE_TTL_MS = 24 * 60 * 60 * 1000;

  function cacheGet(handle) {
    try {
      var raw = localStorage.getItem(CACHE_PREFIX + handle);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || !entry.t || Date.now() - entry.t > CACHE_TTL_MS) return null;
      return entry;
    } catch (_) { return null; }
  }

  function cacheSet(handle, data) {
    try {
      localStorage.setItem(
        CACHE_PREFIX + handle,
        JSON.stringify({ t: Date.now(), profileImage: data.profileImage, vendorName: data.vendorName })
      );
    } catch (_) { /* storage full or blocked — ignore */ }
  }

  /** Pull the worker URL off the script tag that loaded us. */
  function getWorkerUrl() {
    var scripts = document.querySelectorAll(
      'script[data-tbb-curated-by-worker-url]'
    );
    for (var i = 0; i < scripts.length; i++) {
      var u = scripts[i].getAttribute('data-tbb-curated-by-worker-url');
      if (u) return u.replace(/\/$/, '');
    }
    return null;
  }

  /**
   * Apply Cloudinary face-fill transform if the URL is Cloudinary.
   * Otherwise return as-is (defensive — unknown CDNs).
   */
  function transformAvatar(url) {
    if (!url) return url;
    if (url.indexOf('/upload/') !== -1) {
      return url.replace('/upload/', '/upload/w_192,h_192,c_fill,g_face/');
    }
    return url;
  }

  /** Replace placeholder span with a real <img> if no img exists yet. */
  function ensureImg(badge, src, alt) {
    var existing = badge.querySelector('[data-tbb-curated-by-img]');
    if (existing) {
      existing.src = src;
      existing.removeAttribute('srcset');
      if (alt) existing.alt = alt;
      return;
    }

    var placeholder = badge.querySelector('[data-tbb-curated-by-placeholder]');
    var frame = badge.querySelector('.tbb-curated-by__avatar-frame');
    if (!frame) return;

    var img = document.createElement('img');
    img.className = 'tbb-curated-by__avatar';
    img.setAttribute('data-tbb-curated-by-img', '');
    img.src = src;
    img.alt = alt || '';
    // Eager load + high fetch priority — by the time we hydrate, the
    // badge is already in the user's viewport, so lazy hurts more than
    // it helps. Tells the browser this image matters now.
    img.loading = 'eager';
    img.setAttribute('fetchpriority', 'high');
    img.width = 56;
    img.height = 56;

    if (placeholder) {
      frame.replaceChild(img, placeholder);
    } else {
      frame.appendChild(img);
    }
  }

  /** Hydrate one badge instance. */
  function hydrate(badge, workerUrl) {
    if (badge.__tbbCuratedByHydrated) return;
    badge.__tbbCuratedByHydrated = true;

    var handle = badge.getAttribute('data-vendor-handle');
    if (!handle) return;

    // Fast path: serve from localStorage cache synchronously. No network,
    // no flicker. This is what makes repeat PDP visits feel instant.
    var cached = cacheGet(handle);
    if (cached && cached.profileImage) {
      var cachedSrc = transformAvatar(cached.profileImage);
      var cachedAlt =
        cached.vendorName || badge.getAttribute('data-vendor-name') || '';
      ensureImg(badge, cachedSrc, cachedAlt);
      return;
    }

    var endpoint = workerUrl + '/vendor?handle=' + encodeURIComponent(handle);

    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, 4000);

    fetch(endpoint, { signal: controller.signal })
      .then(function (res) {
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error('Worker ' + res.status);
        return res.json();
      })
      .then(function (json) {
        var data = (json && json.data) || json;
        if (!data || !data.profileImage) return;
        var src = transformAvatar(data.profileImage);
        var alt =
          data.vendorName || badge.getAttribute('data-vendor-name') || '';
        ensureImg(badge, src, alt);
        cacheSet(handle, data);
      })
      .catch(function () {
        /* fail-soft — leave the server-rendered avatar in place */
      });
  }

  function run() {
    var workerUrl = getWorkerUrl();
    if (!workerUrl) return;
    var badges = document.querySelectorAll('[data-tbb-curated-by]');
    for (var i = 0; i < badges.length; i++) {
      hydrate(badges[i], workerUrl);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
